import { buildCacheIdentifier } from "../offline/CacheManager";
import { getLyricsParts, LyricsPart } from "./LyricsExtractor";

const CACHE_NAME = buildCacheIdentifier("chordsheetCache");
const CHORDSHEETS_CACHE_PREFIX = "/chordsheets/";

/**
 * Transforms a chordsheet string into structural lyrics parts and persists the result.
 * Stores the transformed parts under a relative URL path key.
 */
export async function transformAndPersistChordsheet(
  id: string,
  chordsheet: string,
): Promise<void> {
  const result = await getLyricsParts(chordsheet);
  if (!Array.isArray(result)) {
    throw new Error(result.message);
  }
  const cacheKey = `${CHORDSHEETS_CACHE_PREFIX}${id}`;
  const response = new Response(JSON.stringify(result), {
    headers: {
      "Content-Type": "application/json",
    },
  });

  const cache = await caches.open(CACHE_NAME);
  await cache.put(cacheKey, response);
}

/**
 * Retrieves the persisted transformed chordsheet parts by id from the Cache API.
 * Returns an empty array if not found.
 */
export async function getPersistedChordsheet(
  id: string,
): Promise<LyricsPart[]> {
  const cacheKey = `${CHORDSHEETS_CACHE_PREFIX}${id}`;
  const cache = await caches.open(CACHE_NAME);
  const response = await cache.match(cacheKey);

  if (!response) {
    return [];
  }

  return response.json() as Promise<LyricsPart[]>;
}

/**
 * Reads all request keys from the chordsheet cache and returns a Set of song IDs
 * that have successfully persisted chordsheet entries.
 */
export async function getPersistedChordsheetIds(): Promise<Set<string>> {
  const cache = await caches.open(CACHE_NAME);
  const keys = await cache.keys();
  const ids = new Set<string>();
  const prefix = CHORDSHEETS_CACHE_PREFIX;
  for (const req of keys) {
    const url = new URL(req.url);
    if (url.pathname.startsWith(prefix)) {
      const id = url.pathname.slice(prefix.length);
      if (id) {
        ids.add(id);
      }
    }
  }
  return ids;
}
